"use client";

import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { FiArrowLeft, FiBookOpen, FiDownload, FiEdit, FiExternalLink, FiRefreshCw } from "react-icons/fi";
import Badge from "@/components/ui/badge";
import Button from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableHeaderRow, TableRow } from "@/components/ui/table";
import Tooltip from "@/components/ui/tooltip";
import { useUser } from "@/contexts/user-provider";
import { useLabVendorModules } from "@/hooks/use-lab-vendor-modules";
import { useTimezone } from "@/hooks/use-timezone";
import { formatDate, formatDateTime, formatToHumanReadable } from "@/lib/format-data";
import { ACCESS_COLLECTION_METHOD_LABELS, COLLECTION_METHOD_LABELS, FASTING_STATUS_LABELS } from "@/lib/lab-order-utils";
import { canCancelOrder, canDownloadResultPdf, getLabStatusMeta, hasCollectionInstructionsAvailable, hasLabelsAvailable, hasRequisitionAvailable } from "@/lib/lab-status";
import { parseError } from "@/lib/parse-error";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import { AccessCollectionGuideDrawer } from "./access-collection-guide";
import LabJunctionEmbed from "./lab-junction-embed";
import LabResultsAISummary from "./lab-results-ai-summary";
import PrintLabelsButton from "./print-labels-button";
import { cancelInHouseOrder, cancelLabOrder, downloadResultPdf, openCollectionInstructionsPdf, openLabelsPdf, openRequisitionPdf, submitAccessOrder, submitInHouseOrder } from "@/api/provider/lab/lab";
import { labQueryKeys, labQueryOptions } from "@/api/provider/lab/lab-query";
interface LabOrderDetailProps {
  orderId: string;
  /** Omitted when hosted in a dialog, whose own close replaces the back link. */
  onBack?: () => void;
  backLabel?: string;
  onEditDraft?: (orderId: string) => void;
}
const BILLING_TYPE_LABELS: Record<string, string> = {
  client_bill: "Client Bill",
  commercial_insurance: "Commercial Insurance",
  patient_bill: "Patient Bill",
  patient_bill_passthrough: "Patient Bill (Passthrough)"
};
function InfoCard(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  if (!value) {
    return null;
  }
  let t1;
  if ($[0] !== label) {
    t1 = <p className="text-caption-strong text-foreground-secondary">{label}</p>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <p className="text-body">{value}</p>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="space-y-1">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
export default function LabOrderDetail({
  orderId,
  onBack,
  backLabel = "Back to orders",
  onEditDraft
}: LabOrderDetailProps) {
  const queryClient = useQueryClient();
  const timeZone = useTimezone();
  const {
    data: order,
    isLoading
  } = useQuery(labQueryOptions.order(orderId));
  const cancelMutation = useMutation({
    // An in-house order is cancelled on its own route: the generic one is behind
    // Junction's module, which a clinic with only its own analyzer doesn't hold.
    mutationFn: () => order?.vendor === "in_house" ? cancelInHouseOrder(orderId) : cancelLabOrder(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: labQueryKeys.all
      });
    },
    // Only the in-house route throws: when the lab didn't get the cancel, the order
    // stays live and staff need to know the analyzer may still run it.
    onError: async error => {
      const message = await parseError(error, "Couldn't cancel this order. Please try again.");
      toast({
        variant: "error",
        title: "Cancel failed",
        description: message
      });
    }
  });

  // Access has no draft-edit endpoint (update_draft_order rejects any
  // non-Junction vendor) — an Access order is authored once and placed in one
  // shot. So a DRAFT/FAILED Access order (the batch-create call succeeded but
  // the follow-up submit didn't) can't be "edited", only resubmitted as-is:
  // retry the same order id, no new draft, no new form.
  const retrySubmitMutation = useMutation({
    // An in-house order is resent the same way: same order id, same order number.
    mutationFn: () => order?.vendor === "in_house" ? submitInHouseOrder(orderId) : submitAccessOrder(orderId),
    onSuccess: updated => {
      if (updated) queryClient.setQueryData(labQueryKeys.order(orderId), updated);
      queryClient.invalidateQueries({
        queryKey: labQueryKeys.all
      });
    },
    onError: async error_0 => {
      const message_0 = await parseError(error_0, order?.vendor === "in_house" ? "Couldn't send this order to the lab. Please try again." : "Couldn't submit this order to Access. Please try again.");
      toast({
        variant: "error",
        title: "Submit failed",
        description: message_0
      });
    }
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isLoadingRequisition, setIsLoadingRequisition] = useState(false);
  const [isLoadingLabels, setIsLoadingLabels] = useState(false);
  const [isAccessGuideOpen, setIsAccessGuideOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"details" | "results" | "junction">("details");

  // Orders outlive the module that made them — the list is not vendor-filtered — so a clinic that
  // offboarded a vendor still opens its old orders here. Anything that would *call* that vendor is
  // gated server-side on the order's own vendor module, so offer it only while the clinic holds it.
  const enabledModules = useLabVendorModules();
  // Keyed by vendor rather than defaulting every non-Junction vendor to Fullscript, so a third
  // vendor is a missing key the type checker catches, not silent inheritance of the wrong gate.
  const orderVendorEnabled = order ? enabledModules[order.vendor] === true : false;

  // Cancelling and editing a draft both write to the order at the vendor, so they need the
  // ordering permission rather than the read one that opened this view. Without the gate a
  // results-only role is shown two buttons whose requests 403.
  const {
    currentUser
  } = useUser();
  const canOrderLabs = hasPermission(currentUser, PERMISSIONS.LABS_ORDERS_WRITE);

  // Both gates live in lab-status now: read as inline prefix comparisons they
  // silently interpreted Fullscript states as Junction ones, which made the
  // results-PDF button never appear on a Fullscript order that had results.
  const canCancel = order ? canCancelOrder(order) && orderVendorEnabled && canOrderLabs : false;
  const canDownloadPdf = order ? canDownloadResultPdf(order) && orderVendorEnabled : false;
  // Gate on the requisition milestone (status/phase), not the granular event:
  // on a fresh order the "requisition_created" status lands before its event,
  // so an events-only check hid the button while the badge already showed it.
  const hasRequisition = order ? hasRequisitionAvailable(order) && orderVendorEnabled : false;
  const hasCollectionInstructions = order ? hasCollectionInstructionsAvailable(order) && orderVendorEnabled : false;
  const collectionInstructions = useQuery({
    ...labQueryOptions.collectionInstructions(orderId),
    enabled: hasCollectionInstructions
  });
  // On-site collection is the only modality where our own staff draw the sample
  // and so need a label for the tube — see hasLabelsAvailable.
  const hasLabels = order ? hasLabelsAvailable(order) && orderVendorEnabled : false;
  const hasAccessCollectionGuide = order?.vendor === "access" && order.accessCollectionMethod === "practice_draw" && orderVendorEnabled;
  // A failed order is recoverable: reopen it in the flow to fix the problem
  // (e.g. a bad demographic) and resubmit in place — same order id, no
  // duplicate. Drafts and failures share the same edit affordance.
  const isFailed = order?.internalStatus === "failed";
  const isStuckDraftOrFailed = order?.internalStatus === "draft" || isFailed;
  // Junction-only: onEditDraft reopens the Junction wizard, which knows
  // nothing about an Access order's fields (no lab_slug, no cart bucket) —
  // opening it on an Access draft would author garbage into the wrong flow.
  const canEditDraft = order?.vendor === "junction" && isStuckDraftOrFailed && !!onEditDraft && orderVendorEnabled && canOrderLabs;
  // Access's and the in-house lab's counterpart to "Edit draft": there is
  // nothing to edit (the draft already carries everything the lab needs), just
  // a resubmit of the same order id — see retrySubmitMutation above.
  const canRetrySubmit = (order?.vendor === "access" || order?.vendor === "in_house" && order.vendorStatus !== "cancelled") && isStuckDraftOrFailed && orderVendorEnabled && canOrderLabs;
  // The Junction App Embed view is scoped to a submitted order's vendor id.
  // Vendor-gated: create_results_embed_url needs a team_id that only lives in a
  // Junction order's vendor_metadata, so it raises for a Fullscript order — the
  // backend already failed correctly, this tab was what offered the dead end.
  const canShowJunction = order?.vendor === "junction" && order?.internalStatus === "submitted" && !!order?.vendorOrderId && orderVendorEnabled;
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([queryClient.refetchQueries({
      queryKey: labQueryKeys.order(orderId)
    }), queryClient.refetchQueries({
      queryKey: labQueryKeys.collectionInstructions(orderId)
    })]);
    setIsRefreshing(false);
  };
  const handleRequisition = async () => {
    setIsLoadingRequisition(true);
    try {
      await openRequisitionPdf(orderId);
    } catch (error_1) {
      console.error("Failed to download requisition:", error_1);
    } finally {
      setIsLoadingRequisition(false);
    }
  };
  const handlePrintLabels = async (numberOfLabels: number) => {
    setIsLoadingLabels(true);
    try {
      await openLabelsPdf(orderId, {
        // The moment of the draw, on the clinic's wall clock. Computed at click
        // time because staff print at the chair and label at the patient's
        // side: a pre-printed stamp is a time that has not happened yet, which
        // inflates apparent specimen age at accessioning and can get a good
        // draw rejected. Deliberately neither `toISOString()` (UTC — a 5pm
        // Pacific draw serialises as the next day) nor the raw browser clock,
        // which ignores the user's timezone override. `sv-SE` is the locale
        // whose default output is already `YYYY-MM-DD HH:MM:SS` on a 24-hour
        // clock, so midnight stays 00 without an hour-24 guard.
        collectionDate: new Date().toLocaleString("sv-SE", {
          timeZone
        }).replace(" ", "T"),
        numberOfLabels
      });
    } catch (error_2) {
      console.error("Failed to open collection labels:", error_2);
      // Louder than the neighbouring handlers on purpose: on-site collection is
      // closed beta at Junction, so a tenant without it gets a 4xx here, and a
      // silent no-op at the chair is the wrong thing to hand a phlebotomist.
      toast({
        title: "Couldn't print labels",
        // `parseError` reads `error.response`, so a thrown client-side Error
        // (pop-up blocked) has no detail to find and would fall through to the
        // generic copy — dropping the one line that says how to fix it. Its own
        // message becomes the fallback; an HTTP error still prefers the body.
        description: await parseError(error_2, error_2 instanceof Error && error_2.message ? error_2.message : "The labels PDF could not be loaded. Try again, or label the tube by hand."),
        variant: "error"
      });
    } finally {
      setIsLoadingLabels(false);
    }
  };
  const handleCollectionInstructions = () => {
    if (!collectionInstructions.data) return;
    try {
      openCollectionInstructionsPdf(collectionInstructions.data);
    } catch (error_3) {
      toast({
        title: "Couldn't open collection instructions",
        description: error_3 instanceof Error ? error_3.message : undefined,
        variant: "error"
      });
    }
  };
  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    try {
      await downloadResultPdf(orderId);
    } catch (error_4) {
      console.error("Failed to download PDF:", error_4);
    } finally {
      setIsDownloading(false);
    }
  };
  if (isLoading || !order) {
    return <div className="space-y-4">
        {onBack && <button onClick={onBack} className="flex items-center gap-2 text-body text-foreground-secondary hover:text-foreground">
            <FiArrowLeft className="h-4 w-4" />
            {backLabel}
          </button>}
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 rounded bg-muted" />
          <div className="h-32 rounded bg-muted" />
        </div>
      </div>;
  }
  const icdCodes = order.icdCodes ?? [];
  const icdNotSentForBilling = order.vendor === "junction" && (order.billingType === "client_bill" || order.billingType === "patient_bill_passthrough") ? BILLING_TYPE_LABELS[order.billingType] : null;
  // The specimen collection the provider entered, shown only for an Access
  // practice draw. That is the one modality whose date is a real draw: for
  // either mobile option Access takes the order time and the collector records
  // the truth in their own system, so a date typed here never reached the lab
  // and showing it would assert a collection that did not happen.
  // An in-house order is always drawn in the clinic, so its collection is always real.
  const accessCollection = (order.vendor === "access" && order.accessCollectionMethod === "practice_draw" || order.vendor === "in_house") && order.collectionDate ? [
  // A calendar day and a wall-clock time the provider typed, which went
  // to the lab verbatim: neither is converted, so the collection stays put.
  formatDate(order.collectionDate), order.collectionTime].filter(Boolean).join(" at ") : undefined;
  const hasResults = order.results.length > 0;
  const statusMeta = getLabStatusMeta(order);
  return <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          {onBack && <button onClick={onBack} className="flex items-center gap-2 text-body text-foreground-secondary hover:text-foreground">
              <FiArrowLeft className="h-4 w-4" />
              {backLabel}
            </button>}
          <div className="flex items-center gap-3">
            <h3 className="text-h2">Lab Order</h3>
            <Badge variant={statusMeta.badgeVariant}>{statusMeta.label}</Badge>
          </div>
          {/* Labelled, because each vendor's identifier is a different thing.
              Fullscript's own lab id is a UUID that appears nowhere the patient
              or Fullscript support can see, so its order number — the reference
              printed on the patient's receipt — is shown instead once the lab has
              been bought. Junction's order id is quotable to Junction, so it
              stays. Access's order number is the 10-character barcode value
              printed on the requisition and every specimen label, so it's
              quotable to Access the same way. An uploaded order has no vendor to
              quote an id to — there is nothing to show here; the lab name already
              renders under "Lab & Tests" below. */}
          {order.vendor === "fullscript" ? order.fullscriptOrderNumber && <p className="text-caption text-foreground-secondary">
                  Order number{" "}
                  <span className="font-mono">
                    {order.fullscriptOrderNumber}
                  </span>
                </p> : order.vendor === "access" ? order.vendorOrderId && <p className="text-caption text-foreground-secondary">
                    Access order number{" "}
                    <span className="font-mono">{order.vendorOrderId}</span>
                  </p> : order.vendor === "in_house" ? order.vendorOrderId && <p className="text-caption text-foreground-secondary">
                      Order number{" "}
                      <span className="font-mono">{order.vendorOrderId}</span>
                      {/* Whether the analyzer can find the tube: it has the order
                          once the lab interface has acknowledged it. */}
                      {order.vendorStatus === "received" && <span> · The lab has the order</span>}
                    </p> : order.vendor === "junction" && order.vendorOrderId && <p className="text-caption text-foreground-secondary">
                      Junction order ID{" "}
                      <span className="font-mono">{order.vendorOrderId}</span>
                    </p>}
        </div>
        <div className="group flex items-center gap-2">
          <Button variant="ghost" size="icon-sm" disabled={isRefreshing} onClick={handleRefresh} aria-label="Refresh lab order"
        // Hidden until the button cluster is hovered (or focused), then
        // fades in muted with no background. Stays visible while refreshing
        // so the spinner remains discoverable after the cursor leaves.
        className={cx("text-foreground-muted transition-[color,opacity] md:hover:bg-transparent md:hover:text-foreground", !isRefreshing && "opacity-0 focus-visible:opacity-100 group-hover:opacity-100")} icon={<FiRefreshCw className={cx("size-4", isRefreshing && "animate-spin")} />} />
          {hasRequisition && <Button variant="light" size="sm" onClick={handleRequisition} isLoading={isLoadingRequisition}>
              <FiExternalLink className="mr-1.5 h-3.5 w-3.5" />
              Requisition
            </Button>}
          {hasCollectionInstructions && <Tooltip content={collectionInstructions.error ? collectionInstructions.error.message : collectionInstructions.data === null ? "The lab is still preparing the collection instructions. Check back in a few minutes." : undefined} triggerAsChild>
              <span className="inline-block">
                <Button variant="light" size="sm" onClick={handleCollectionInstructions} isLoading={collectionInstructions.isLoading} disabled={!collectionInstructions.data}>
                  <FiExternalLink className="mr-1.5 h-3.5 w-3.5" />
                  Collection instructions
                </Button>
              </span>
            </Tooltip>}
          {hasAccessCollectionGuide && <Button variant="light" size="sm" onClick={() => setIsAccessGuideOpen(true)}>
              <FiBookOpen className="mr-1.5 h-3.5 w-3.5" />
              Collection instructions
            </Button>}
          {canRetrySubmit && <Button variant="light" size="sm" onClick={() => retrySubmitMutation.mutate()} isLoading={retrySubmitMutation.isPending}>
              <FiRefreshCw className="mr-1.5 h-3.5 w-3.5" />
              <span>{isFailed ? "Retry submit" : "Submit"}</span>
            </Button>}
          {hasLabels && <PrintLabelsButton onPrint={handlePrintLabels} isLoading={isLoadingLabels} />}
          {canEditDraft && <Button variant="light" size="sm" onClick={() => onEditDraft?.(order.id)}>
              <FiEdit className="mr-1.5 h-3.5 w-3.5" />
              <span>{isFailed ? "Edit & resubmit" : "Edit draft"}</span>
            </Button>}
          {canDownloadPdf && <Button variant="light" size="sm" onClick={handleDownloadPdf} isLoading={isDownloading}>
              <FiDownload className="mr-1.5 h-3.5 w-3.5" />
              Results PDF
            </Button>}
          {canCancel && <Button variant="light" size="sm" onClick={() => cancelMutation.mutate()} isLoading={cancelMutation.isPending} className="text-status-error-text md:hover:bg-status-error-bg md:hover:text-status-error-text">
              Cancel Order
            </Button>}
        </div>
      </div>

      {/* Error Message */}
      {order.errorMessage && <div className="rounded-lg border border-status-error-text/20 bg-status-error-bg p-4">
          <p className="text-body-strong text-status-error-text">
            Submission Error
          </p>
          <p className="mt-1 text-body text-status-error-text">
            {order.errorMessage}
          </p>
          {/* No demographics link here: malformed demographics are caught
            up front (before an order exists), so a persisted failure is a
            lab/order rejection. "Edit & resubmit" above recovers it. */}
        </div>}

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex items-center justify-between">
          {/* Left group: native Details + Results tabs (unchanged) */}
          <div className="flex gap-0">
            <button type="button" onClick={() => setActiveTab("details")} className={cx("px-4 py-2 text-body-strong transition-colors", activeTab === "details" ? "border-b-2 border-primary text-foreground" : "text-foreground-secondary hover:text-foreground")}>
              Order Details
            </button>
            {hasResults && <button type="button" onClick={() => setActiveTab("results")} className={cx("flex items-center gap-2 px-4 py-2 text-body-strong transition-colors", activeTab === "results" ? "border-b-2 border-primary text-foreground" : "text-foreground-secondary hover:text-foreground")}>
                Results
                <Badge variant="success" size="xs">
                  {order.results.length}
                </Badge>
              </button>}
          </div>
          {/* Right edge: Junction App Embed view, visually separated */}
          {canShowJunction && <button type="button" onClick={() => setActiveTab("junction")} className={cx("px-4 py-2 text-body-strong transition-colors", activeTab === "junction" ? "border-b-2 border-primary text-foreground" : "text-foreground-secondary hover:text-foreground")}>
              Junction View
            </button>}
        </div>
      </div>

      {/* Order Details Tab */}
      {activeTab === "details" && <div className="space-y-6">
          {/* Order Timeline */}
          {(() => {
        const events = order.events ?? [];
        if (events.length === 0) return null;
        return <div className="rounded-lg border border-border p-4">
                <p className="mb-3 text-body-strong">Order Timeline</p>
                <div className="relative space-y-0">
                  {[...events].reverse().map((event, idx) => {
              const label = formatToHumanReadable((event.statusCode ?? "").split(".").pop() ?? event.statusCode ?? "");
              const isLatest = idx === 0;
              const isLast = idx === events.length - 1;
              return <div key={event.id ?? idx} className="flex items-start gap-3">
                        {/* Stretch to the row so the line reaches the next dot, not just its own minimum. */}
                        <div className="flex flex-col items-center self-stretch">
                          <div className={cx("mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full", isLatest ? "bg-status-success-text" : "bg-foreground-muted")} />
                          {!isLast &&
                  // -mb-1 meets the next dot, which sits mt-1 below its row's top.
                  <div className="-mb-1 w-px flex-1 bg-border" />}
                        </div>
                        <div className="pb-4">
                          <p className={cx("text-body", isLatest ? "font-medium" : "text-foreground-secondary")}>
                            {label}
                          </p>
                          {event.eventTimestamp && <p className="text-caption text-foreground-muted">
                              {formatDateTime(new Date(event.eventTimestamp))}
                            </p>}
                        </div>
                      </div>;
            })}
                </div>
              </div>;
      })()}

          {/* Two-column: Lab & Tests + Order Information */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Lab & Tests */}
            <div className="rounded-lg border border-border p-4">
              <p className="mb-3 text-body-strong">Lab & Tests</p>
              <div className="space-y-3">
                <InfoCard label="Laboratory" value={order.labName || order.labSlug} />
                {order.items.length > 0 && <div className="space-y-1">
                    <p className="text-caption-strong text-foreground-secondary">
                      Tests Ordered
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {order.items.map(item => <span key={item.id} className="rounded bg-background-secondary px-2 py-0.5 text-caption">
                          {item.displayName || "Test"}
                        </span>)}
                    </div>
                  </div>}
                <InfoCard label="Sample Type" value={order.sampleType} />
                <InfoCard label="Fasting Required" value={order.fasting === "true" ? "Yes" : order.fasting === "false" ? "No" : undefined} />
                <InfoCard label="Collection Method" value={order.vendor === "access" ? order.accessCollectionMethod && ACCESS_COLLECTION_METHOD_LABELS[order.accessCollectionMethod] : order.collectionMethod ? COLLECTION_METHOD_LABELS[order.collectionMethod] : undefined} />
                <InfoCard label="Collection Date" value={accessCollection} />
                <InfoCard label="Fasting" value={order.fastingStatus && FASTING_STATUS_LABELS[order.fastingStatus]} />
              </div>
            </div>

            {/* Order Information */}
            <div className="rounded-lg border border-border p-4">
              <p className="mb-3 text-body-strong">Order Information</p>
              <div className="space-y-3">
                {/* An uploaded order has no clinic physician — the ordering provider is
                    external and arrives as free text with the results, so it is labelled for
                    what it is rather than rendered as our own physician. Without this the card
                    read empty even though the name was captured and stored. */}
                {order.vendor === "uploaded" ? <InfoCard label="Ordering provider (from report)" value={order.orderingProviderName || undefined} /> : <InfoCard label="Physician" value={[order.physicianFirstName, order.physicianLastName].filter(Boolean).join(" ") + (order.physicianNpi ? ` (NPI: ${order.physicianNpi})` : "") || undefined} />}
                {/* Orders sent before accounts were recorded on them have none. */}
                {order.vendor === "access" && order.accessClientNumber && <InfoCard label="Access Client Number" value={order.accessClientNumber} />}
                <InfoCard label="Billing Type" value={order.billingType ? BILLING_TYPE_LABELS[order.billingType] : undefined} />
                {icdCodes.length > 0 && <div className="space-y-1">
                    <p className="text-caption-strong text-foreground-secondary">
                      ICD-10 Codes
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {icdCodes.map(code => <span key={code} className="rounded bg-background-secondary px-2 py-0.5 font-mono text-caption">
                          {code}
                        </span>)}
                    </div>
                    {icdNotSentForBilling && <p className="text-caption text-foreground-muted">
                        Not sent to the lab for {icdNotSentForBilling}, so they
                        won&apos;t appear on the requisition.
                      </p>}
                  </div>}
                <InfoCard label="Priority" value={order.priority === "true" || order.priority === "True" ? "STAT" : "Standard"} />
                <InfoCard label="Clinical Notes" value={order.clinicalNotes} />
                <InfoCard label="Created" value={order.createdDate ? formatDateTime(order.createdDate) : undefined} />
              </div>
            </div>
          </div>
        </div>}

      {/* Results Tab */}
      {activeTab === "results" && hasResults && <>
          <LabResultsAISummary results={order.results} order={order} />
          <Table>
            <TableHead>
              <TableHeaderRow>
                <TableHeaderCell className="w-[25%]">Biomarker</TableHeaderCell>
                <TableHeaderCell className="w-[15%]">Value</TableHeaderCell>
                <TableHeaderCell className="w-[10%]">Unit</TableHeaderCell>
                <TableHeaderCell className="w-[20%]">Ref Range</TableHeaderCell>
                <TableHeaderCell className="w-[10%]">Status</TableHeaderCell>
                <TableHeaderCell className="w-[20%]">Notes</TableHeaderCell>
              </TableHeaderRow>
            </TableHead>
            <TableBody className="divide-y-[0.5px]">
              {order.results.map(result => {
            const isAbnormal = result.clinicalInterpretation === "abnormal" || result.clinicalInterpretation === "critical";
            return <TableRow key={result.id}>
                    <TableCell>
                      <span className="text-body-strong">
                        {result.markerName}
                      </span>
                      {result.resultStatus === "corrected" && <Badge variant="warning" size="xs" className="ml-2">
                          Corrected
                        </Badge>}
                    </TableCell>
                    <TableCell>
                      <span className={cx("text-body", isAbnormal && "font-medium text-status-error-text")}>
                        {result.valueDisplay || "-"}
                      </span>
                      {result.abnormalFlag && result.abnormalFlag.toUpperCase() !== "N" && <span className={cx("ml-1.5 text-caption-strong", isAbnormal ? "text-status-error-text" : "text-foreground-secondary")}>
                            {result.abnormalFlag.toUpperCase()}
                          </span>}
                    </TableCell>
                    <TableCell>
                      <span className="text-body text-foreground-secondary">
                        {result.unit || "-"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-body text-foreground-secondary">
                        {result.referenceRangeText ? result.referenceRangeText : result.minRangeValue != null && result.maxRangeValue != null ? `${result.minRangeValue} - ${result.maxRangeValue}` : "-"}
                      </span>
                    </TableCell>
                    <TableCell>
                      {result.clinicalInterpretation && <Badge variant={isAbnormal ? "error" : "success"} size="xs">
                          {result.clinicalInterpretation}
                        </Badge>}
                    </TableCell>
                    <TableCell>
                      <span className="text-body text-foreground-secondary">
                        {result.notes || "-"}
                      </span>
                    </TableCell>
                  </TableRow>;
          })}
            </TableBody>
          </Table>
        </>}

      {/* Junction App Embed Tab */}
      {activeTab === "junction" && canShowJunction && <LabJunctionEmbed orderId={orderId} />}

      {hasAccessCollectionGuide && <AccessCollectionGuideDrawer orderId={orderId} open={isAccessGuideOpen} onOpenChange={setIsAccessGuideOpen} />}
    </div>;
}